import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { serviceSchema } from '../lib/structuredData';
import {
  AlertTriangle, Phone, Siren, Clock, ShieldCheck, Wrench, Zap, Snowflake,
  Droplets, Wind, ArrowRight, CheckCircle2,
} from 'lucide-react';

/**
 * Repair & Breakdown — emergency-led service page.
 * Visual tone: urgent (red alert banner) but professional underneath.
 */
const EMERGENCY_NUMBER_TEL = '+442080599941';
const EMERGENCY_NUMBER_DISPLAY = '020 8059 9941';

const RepairBreakdown = () => {
  const symptoms = [
    { icon: <Snowflake className="w-8 h-8" />, title: 'Not cooling or heating', desc: 'Unit runs but air temperature never changes — typically a refrigerant or compressor fault.' },
    { icon: <Droplets className="w-8 h-8" />, title: 'Leaking water', desc: 'Condensate pouring from the indoor unit — risk of ceiling, plaster or stock damage.' },
    { icon: <Wind className="w-8 h-8" />, title: 'No airflow', desc: 'Fan won\'t spin or air is barely moving — usually motor, capacitor or PCB.' },
    { icon: <Zap className="w-8 h-8" />, title: 'Tripping electrics', desc: 'AC trips the breaker repeatedly — short or earth-fault, hazardous if left.' },
    { icon: <AlertTriangle className="w-8 h-8" />, title: 'Burning smell or smoke', desc: 'Stop using immediately and isolate. We treat this as a Priority-1 call-out.' },
    { icon: <Wrench className="w-8 h-8" />, title: 'Error codes & E-faults', desc: 'Flashing LEDs or codes on remote — we read every Mitsubishi, Daikin, LG, Samsung, Toshiba, Panasonic and Fujitsu fault.' },
  ];

  const promises = [
    { icon: <Clock className="w-7 h-7" />, title: 'Same-day London call-out', desc: 'Most central-London emergencies attended within 2–4 hours.' },
    { icon: <ShieldCheck className="w-7 h-7" />, title: 'F-Gas certified engineers', desc: 'Refrigerant work performed lawfully and to UK regulations.' },
    { icon: <CheckCircle2 className="w-7 h-7" />, title: 'Diagnosis first, fix second', desc: 'No parts replaced until we\'ve confirmed the root cause.' },
    { icon: <Wrench className="w-7 h-7" />, title: '12-month repair warranty', desc: 'Every repair is backed for a year — labour and replacement parts.' },
  ];

  const process = [
    { step: '1', title: 'Phone Triage', desc: 'Tell us the brand, error code and what you can hear — we triage severity.' },
    { step: '2', title: 'Dispatch', desc: 'Engineer assigned with the right parts loaded for your system.' },
    { step: '3', title: 'Diagnose', desc: 'On-site fault-find: refrigerant pressures, electrical, control board, sensors.' },
    { step: '4', title: 'Quote & Authorise', desc: 'Fixed-price quote signed off before any chargeable work begins.' },
    { step: '5', title: 'Repair & Test', desc: 'Replacement, recharge, vacuum & full system test before sign-off.' },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Emergency AC Repair & Breakdown — London"
        description="24/7 emergency air-conditioning repair in London. F-Gas certified engineers, same-day call-out, all major brands. No-cool, leaking, error codes — we fix them all."
        path="/repair"
        jsonLd={serviceSchema(
          'Air Conditioning Repair & Breakdown',
          'Emergency same-day AC repair and breakdown service across London. F-Gas certified, all brands.'
        )}
      />

      {/* Hero */}
      <section
        className="relative min-h-[60vh] flex items-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(10, 14, 20, 0.55), rgba(10, 14, 20, 0.75)), url('/images/hero-services.jpg')`,
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <span className="inline-flex items-center gap-2 text-xs bg-red-600/15 px-4 py-2 rounded-full text-red-300 font-bold border border-red-500/30 tracking-wider mb-6">
            <Siren className="w-3.5 h-3.5 animate-pulse" /> 24/7 EMERGENCY CALL-OUT
          </span>
          <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-6">
            AC <span className="text-red-500">Repair</span> & Breakdown London
          </h1>
          <p className="text-gray-300 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto mb-8">
            Cooling failure on a shop floor. Leaking unit above a server room. Sparking PCB at midnight. We get an F-Gas engineer to you fast — and we fix it properly the first time.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <a href={`tel:${EMERGENCY_NUMBER_TEL}`} data-testid="repair-hero-emergency-call">
              <Button className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-6 rounded-md text-base shadow-lg shadow-red-600/40">
                <Phone className="mr-2" size={20} /> Call Now {EMERGENCY_NUMBER_DISPLAY}
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" className="w-full sm:w-auto border-white/30 hover:bg-white/10 text-white font-bold px-8 py-6 rounded-md text-base">
                Request Call Back
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Symptoms */}
      <section className="py-20 bg-[#0A0E14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-red-500 font-bold text-xs tracking-[3px] uppercase mb-3">COMMON FAULTS WE FIX</p>
            <h2 className="text-4xl md:text-5xl font-bold">What's wrong with your unit?</h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              If any of these sound like what you're seeing — call us before the issue spreads.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {symptoms.map((s, idx) => (
              <div
                key={idx}
                className="group bg-[#121820] border border-white/10 rounded-2xl p-6 hover:border-red-500/40 hover:-translate-y-1 transition-all"
                data-testid={`repair-symptom-${idx}`}
              >
                <div className="w-14 h-14 rounded-xl bg-red-600/10 border border-red-500/20 grid place-items-center mb-5 text-red-400">
                  {s.icon}
                </div>
                <h3 className="text-lg font-bold mb-2 group-hover:text-red-400 transition-colors">{s.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our promises */}
      <section className="py-20 bg-[#121820]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {promises.map((p, idx) => (
              <div key={idx} className="flex flex-col items-start gap-3" data-testid={`repair-promise-${idx}`}>
                <div className="w-12 h-12 rounded-lg bg-[#FF8C00]/10 border border-[#FF8C00]/30 grid place-items-center text-[#FF8C00]">
                  {p.icon}
                </div>
                <h3 className="text-base font-bold">{p.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-[#0A0E14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF8C00] font-bold text-xs tracking-[3px] uppercase mb-3">OUR REPAIR PROCESS</p>
            <h2 className="text-4xl md:text-5xl font-bold">How we get you running again</h2>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {process.map((item, idx) => (
              <div key={idx} className="text-center relative">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-600 flex items-center justify-center text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
                {idx < process.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-8 -right-3 text-red-500" size={20} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-red-700 to-red-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Siren className="w-12 h-12 mx-auto mb-4 text-white animate-pulse" />
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Need an engineer right now?</h2>
          <p className="text-white/90 text-lg mb-8">London emergency call-outs typically attended within 2–4 hours.</p>
          <a href={`tel:${EMERGENCY_NUMBER_TEL}`} data-testid="repair-cta-emergency-call">
            <Button className="bg-white hover:bg-white/90 text-red-700 font-bold px-10 py-6 rounded-md text-base">
              <Phone className="mr-2" size={20} /> {EMERGENCY_NUMBER_DISPLAY}
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default RepairBreakdown;
